import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/i18n";
import { getDailyRates, ZAVIA_CONFIG } from "@/services/zaviaRates";
import { usePopoverFlip } from "@/components/booking/usePopoverFlip";

const MONTHS = {
    es: ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"],
    en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
};
const DAYS = {
    es: ["lu", "ma", "mi", "ju", "vi", "sá", "do"],
    en: ["mo", "tu", "we", "th", "fr", "sa", "su"],
};

const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromISO = (s) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d); // local, sin UTC
};

function monthCells(year, month) {
    const first = new Date(year, month, 1);
    const offset = (first.getDay() + 6) % 7; // lunes = 0
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    return cells;
}

export default function RangeCalendar({ start, end, onChange, onClose, className = "" }) {
    const { lang, t } = useLang();
    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);
    const [view, setView] = useState(() => {
        const base = start ? fromISO(start) : today;
        return new Date(base.getFullYear(), base.getMonth(), 1);
    });
    const [hover, setHover] = useState(null);
    const [rates, setRates] = useState(null);
    const [loadingRates, setLoadingRates] = useState(false);
    const ref = useRef(null);
    const flip = usePopoverFlip(ref);

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        const onDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onClose();
        };
        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onDown);
        document.addEventListener("touchstart", onDown);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("touchstart", onDown);
        };
    }, [onClose]);

    // Capa de tarifas futura: hoy devuelve null y no se muestra ningún precio.
    useEffect(() => {
        let alive = true;
        setLoadingRates(true);
        const from = toISO(view);
        const to = toISO(new Date(view.getFullYear(), view.getMonth() + 2, 0));
        getDailyRates({ from, to, adults: 2, children: 0, lang, timezone: ZAVIA_CONFIG.timezone })
            .then((r) => alive && setRates(r))
            .catch(() => alive && setRates(null))
            .finally(() => alive && setLoadingRates(false));
        return () => {
            alive = false;
        };
    }, [view, lang]);

    const startD = start ? fromISO(start) : null;
    const endD = end ? fromISO(end) : null;

    const pick = (d) => {
        const iso = toISO(d);
        if (!startD || endD || iso <= start) {
            onChange(iso, "");
            return;
        }
        onChange(start, iso);
        onClose();
    };

    const months = [view, new Date(view.getFullYear(), view.getMonth() + 1, 1)];

    const dayState = (d) => {
        const iso = toISO(d);
        const isStart = start === iso;
        const isEnd = end === iso;
        const rangeEnd = endD || (startD && hover ? hover : null);
        const inRange = startD && rangeEnd && d > startD && d < rangeEnd;
        const disabled = d < today;
        const rate = rates ? rates[iso] : null;
        const unavailable = rates ? !rate || !rate.available : false;
        return { iso, isStart, isEnd, inRange, disabled: disabled || unavailable, rate };
    };

    const renderMonth = (m, idx) => {
        const cells = monthCells(m.getFullYear(), m.getMonth());
        return (
            <div key={idx} className={idx === 1 ? "hidden sm:block" : ""}>
                <div className="flex items-center justify-between px-1 pb-2">
                    {idx === 0 ? (
                        <button
                            type="button"
                            onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
                            disabled={view.getFullYear() === today.getFullYear() && view.getMonth() === today.getMonth()}
                            aria-label="Mes anterior"
                            data-testid="cal-prev-month"
                            className="rounded-full p-2 text-olive hover:bg-sand disabled:opacity-30"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                    ) : (
                        <span className="w-8" />
                    )}
                    <p className="font-display text-sm font-medium capitalize text-ink sm:text-base">
                        {MONTHS[lang][m.getMonth()]} {m.getFullYear()}
                    </p>
                    {idx === months.length - 1 ? (
                        <button type="button" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))} aria-label="Mes siguiente" data-testid="cal-next-month" className="rounded-full p-2 text-olive hover:bg-sand">
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    ) : (
                        <button type="button" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))} aria-label="Mes siguiente" data-testid="cal-next-month-mobile" className="rounded-full p-2 text-olive hover:bg-sand sm:invisible">
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    )}
                </div>
                <div className="grid grid-cols-7 gap-0.5">
                    {DAYS[lang].map((d) => (
                        <span key={d} className="pb-1 text-center text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
                            {d}
                        </span>
                    ))}
                    {cells.map((d, i) => {
                        if (!d) return <span key={`e${i}`} />;
                        const s = dayState(d);
                        return (
                            <button
                                key={s.iso}
                                type="button"
                                disabled={s.disabled}
                                onClick={() => pick(d)}
                                onMouseEnter={() => startD && !endD && setHover(d)}
                                aria-label={s.iso}
                                aria-pressed={s.isStart || s.isEnd}
                                data-testid={`cal-day-${s.iso}`}
                                className={`flex h-9 flex-col items-center justify-center rounded-lg text-xs transition-colors duration-150 sm:h-11 sm:text-sm ${
                                    s.isStart || s.isEnd
                                        ? "bg-olive font-semibold text-cream"
                                        : s.inRange
                                          ? "bg-olive/15 text-ink"
                                          : s.disabled
                                            ? "text-ink-muted/30"
                                            : "text-ink hover:bg-sand"
                                }`}
                            >
                                <span>{d.getDate()}</span>
                                {s.rate && s.rate.available && (
                                    <span className="text-[9px] font-medium text-amber-dark">
                                        ${s.rate.rate} {s.rate.currency}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div
            ref={ref}
            role="dialog"
            aria-label={lang === "en" ? "Select dates" : "Seleccionar fechas"}
            data-testid="range-calendar"
            className={`absolute left-0 z-[95] w-full min-w-0 rounded-2xl border border-sand-border bg-cream p-3 shadow-[0_24px_60px_rgba(45,51,25,0.25)] sm:w-max sm:min-w-[320px] sm:p-4 ${flip ? "bottom-full mb-2" : "top-full mt-2"} ${className}`}
        >
            {loadingRates && (
                <p className="pb-1 text-right text-[10px] font-medium text-ink-muted/60" data-testid="cal-rates-loading">
                    …
                </p>
            )}
            <div className="grid gap-6 sm:grid-cols-2">{months.map(renderMonth)}</div>
        </div>
    );
}
